import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  Buildings, Users, ChartBar, CheckCircle, CurrencyDollar, Globe, DeviceMobile,
  SignOut, Prohibit, ArrowCounterClockwise, X, SpinnerGap, Broadcast,
} from "@phosphor-icons/react";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#141418] border border-white/10 rounded-xl p-5" data-testid={`sa-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={20} weight="duotone" className="text-[#14B8A6] mb-3" />
      <div className="text-3xl font-display font-bold tracking-tight tabular-nums text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
      {sub != null && <div className="text-xs text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

const planColor = { pro: "#BF953F", starter: "#1F6FE5", free: "#71717a", platform: "#14B8A6" };

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [trends, setTrends] = useState(null);
  const [trendDays, setTrendDays] = useState(30);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, o, tr] = await Promise.all([
        api.get("/superadmin/analytics"),
        api.get("/superadmin/organisations"),
        api.get("/superadmin/trends?days=30"),
      ]);
      setAnalytics(a.data);
      setOrgs(o.data);
      setTrends(tr.data.series);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, []);

  const loadTrends = useCallback(async (days) => {
    setTrendDays(days);
    try {
      const { data } = await api.get(`/superadmin/trends?days=${days}`);
      setTrends(data.series);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => {
    if (user === false) { nav("/auth"); return; }
    if (user && user.role !== "superadmin") { nav("/app"); return; }
    if (user && user.role === "superadmin") load();
  }, [user, nav, load]);

  const openDetail = async (orgId) => {
    setDetail({ loading: true });
    try {
      const { data } = await api.get(`/superadmin/organisations/${orgId}`);
      setDetail(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setDetail(null); }
  };

  const doSuspend = async (org, reasonText) => {
    setBusy(org.id);
    try {
      await api.post(`/superadmin/organisations/${org.id}/suspend`, { reason: reasonText || "" });
      toast.success(`${org.name} suspended`);
      await load();
      if (detail?.org?.id === org.id) openDetail(org.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const toggleSuspend = async (org) => {
    if (!org.suspended) { setReason(""); setSuspendTarget(org); return; }
    setBusy(org.id);
    try {
      await api.post(`/superadmin/organisations/${org.id}/unsuspend`);
      toast.success(`${org.name} re-activated`);
      await load();
      if (detail?.org?.id === org.id) openDetail(org.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!analytics || !orgs) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-500 font-mono text-sm">loading platform…</div>;

  const es = analytics.elections_by_status || {};

  return (
    <div className="min-h-screen bg-[#09090B] text-white grain" data-testid="superadmin-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#09090B]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo dark />
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-[#14B8A6] border-l border-white/10 pl-4">Platform Console</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-tight">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">super admin</div>
            </div>
            <button data-testid="sa-logout" onClick={() => { logout(); nav("/"); }} className="text-zinc-500 hover:text-red-400 transition-colors" title="Sign out">
              <SignOut size={20} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 relative z-10">
        <h1 className="font-display font-black text-3xl tracking-tighter mb-1">Platform analytics</h1>
        <p className="text-sm text-zinc-500 mb-8">Cross-tenant overview of SelectFlow. {analytics.billing_mock && <span className="text-amber-400 font-mono text-xs">· BILLING MOCKED</span>}</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Stat icon={Buildings} label="Organisations" value={analytics.organisations} sub={`${analytics.suspended_orgs} suspended`} />
          <Stat icon={Users} label="Users" value={analytics.users} />
          <Stat icon={ChartBar} label="Elections" value={analytics.elections_total} sub={`${es.live || 0} live · ${es.closed || 0} closed`} />
          <Stat icon={CheckCircle} label="Votes cast" value={analytics.votes_total} sub={`of ${analytics.voters_total} voters`} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          <Stat icon={Globe} label="Web votes" value={analytics.channel_split.web} />
          <Stat icon={DeviceMobile} label="USSD votes" value={analytics.channel_split.ussd} />
          <Stat icon={CurrencyDollar} label="Revenue (USD)" value={`$${analytics.revenue_usd}`} sub={`${analytics.payments_count} payments`} />
          <Stat icon={Broadcast} label="Live now" value={es.live || 0} sub="elections open" />
        </div>

        {/* 30-day trends */}
        {trends && (
          <div className="bg-[#141418] border border-white/10 rounded-xl p-5 mb-10" data-testid="sa-trends">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Last {trendDays} days · sign-ups & votes</div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1 bg-black/40 rounded-md p-0.5" data-testid="sa-trend-range">
                  {[7, 30, 90].map((d) => (
                    <button key={d} data-testid={`sa-trend-${d}`} onClick={() => loadTrends(d)}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${trendDays === d ? "bg-[#14B8A6] text-black" : "text-zinc-400 hover:text-white"}`}>
                      {d}d
                    </button>
                  ))}
                </div>
                <div className="hidden sm:flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#14B8A6]" /> Sign-ups</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#1F6FE5]" /> Votes</span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trends} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gSignups" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#14B8A6" stopOpacity={0.5} /><stop offset="100%" stopColor="#14B8A6" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gVotes" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1F6FE5" stopOpacity={0.5} /><stop offset="100%" stopColor="#1F6FE5" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" minTickGap={24} axisLine={{ stroke: "#ffffff10" }} tickLine={false} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #ffffff20", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} />
                <Area type="monotone" dataKey="signups" stroke="#14B8A6" strokeWidth={2} fill="url(#gSignups)" name="Sign-ups" />
                <Area type="monotone" dataKey="votes" stroke="#1F6FE5" strokeWidth={2} fill="url(#gVotes)" name="Votes" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Plan distribution */}
        <div className="bg-[#141418] border border-white/10 rounded-xl p-5 mb-10">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Plan distribution</div>
          <div className="flex flex-wrap gap-4">
            {Object.entries(analytics.plan_distribution).map(([plan, n]) => (
              <div key={plan} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: planColor[plan] || "#71717a" }} />
                <span className="text-sm capitalize">{plan}</span>
                <span className="font-mono text-sm text-zinc-400">{n}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Organisations table */}
        <h2 className="font-display font-bold text-xl tracking-tight mb-4">Organisations</h2>
        <div className="bg-[#141418] border border-white/10 rounded-xl overflow-hidden" data-testid="sa-org-table">
          <div className="hidden md:grid grid-cols-[1.6fr_1fr_0.7fr_0.7fr_0.7fr_1fr] gap-3 px-5 py-3 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
            <div>Organisation</div><div>Admin</div><div>Plan</div><div>Elections</div><div>Votes</div><div className="text-right">Action</div>
          </div>
          {orgs.map((o) => (
            <div key={o.id} data-testid={`sa-org-row-${o.id}`} className={`grid grid-cols-2 md:grid-cols-[1.6fr_1fr_0.7fr_0.7fr_0.7fr_1fr] gap-3 px-5 py-3.5 items-center border-b border-white/5 hover:bg-white/[0.02] transition-colors ${o.suspended ? "opacity-60" : ""}`}>
              <button onClick={() => openDetail(o.id)} className="text-left min-w-0" data-testid={`sa-org-name-${o.id}`}>
                <div className="font-medium truncate flex items-center gap-2">{o.name}{o.suspended && <span className="text-[9px] uppercase tracking-widest text-red-400 border border-red-400/40 rounded px-1.5 py-0.5">Suspended</span>}</div>
                <div className="text-xs text-zinc-500 truncate md:hidden">{o.admin_email}</div>
              </button>
              <div className="text-sm text-zinc-400 truncate hidden md:block">{o.admin_email || "—"}</div>
              <div className="hidden md:block"><span className="text-xs capitalize font-medium" style={{ color: planColor[o.plan] }}>{o.plan}</span></div>
              <div className="font-mono text-sm text-zinc-300 hidden md:block">{o.elections}</div>
              <div className="font-mono text-sm text-zinc-300 hidden md:block">{o.votes}</div>
              <div className="flex justify-end">
                <button
                  data-testid={`sa-suspend-${o.id}`}
                  onClick={() => toggleSuspend(o)}
                  disabled={busy === o.id}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50 ${o.suspended ? "border border-[#14B8A6]/40 text-[#14B8A6] hover:bg-[#14B8A6]/10" : "border border-red-400/40 text-red-400 hover:bg-red-400/10"}`}
                >
                  {busy === o.id ? <SpinnerGap size={13} className="animate-spin" /> : o.suspended ? <ArrowCounterClockwise size={13} /> : <Prohibit size={13} />}
                  {o.suspended ? "Reactivate" : "Suspend"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Drill-down modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)} data-testid="sa-detail-modal">
          <div className="bg-[#141418] border border-white/10 rounded-xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            {detail.loading ? (
              <div className="p-10 text-center text-zinc-500 font-mono text-sm">loading…</div>
            ) : (
              <>
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                  <div>
                    <h3 className="font-display font-bold text-xl tracking-tight">{detail.org.name}</h3>
                    <p className="text-xs text-zinc-500 font-mono">{detail.org.plan} · {detail.stats.users} members · created {detail.org.created_at ? new Date(detail.org.created_at).toLocaleDateString() : "—"}</p>
                  </div>
                  <button onClick={() => setDetail(null)} className="text-zinc-500 hover:text-white" data-testid="sa-detail-close"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-3 gap-3">
                    <Stat icon={ChartBar} label="Elections" value={detail.stats.elections} />
                    <Stat icon={Users} label="Voters" value={detail.stats.voters} />
                    <Stat icon={CheckCircle} label="Votes" value={detail.stats.votes} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Elections</div>
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                      {detail.elections.length === 0 && <p className="text-sm text-zinc-500">No elections.</p>}
                      {detail.elections.map((el) => (
                        <div key={el.id} className="flex items-center justify-between gap-3 bg-black/30 rounded-md px-3 py-2 text-sm">
                          <span className="truncate">{el.title}</span>
                          <span className="flex items-center gap-3 shrink-0">
                            <span className="font-mono text-xs text-zinc-400">{el.turnout.pct}%</span>
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500">{el.status}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t border-white/10 pt-4">
                    <div className="text-sm text-zinc-400">{detail.org.suspended ? "This organisation is suspended and cannot log in." : "Organisation is active."}</div>
                    <button
                      data-testid="sa-detail-suspend"
                      onClick={() => toggleSuspend({ id: detail.org.id, name: detail.org.name, suspended: detail.org.suspended })}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${detail.org.suspended ? "border border-[#14B8A6]/40 text-[#14B8A6] hover:bg-[#14B8A6]/10" : "border border-red-400/40 text-red-400 hover:bg-red-400/10"}`}
                    >
                      {detail.org.suspended ? <><ArrowCounterClockwise size={15} /> Reactivate</> : <><Prohibit size={15} /> Suspend</>}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Suspend reason dialog */}
      {suspendTarget && (
        <div className="fixed inset-0 z-[55] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSuspendTarget(null)} data-testid="sa-suspend-dialog">
          <div className="bg-[#141418] border border-white/10 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2"><Prohibit size={18} className="text-red-400" /> Suspend {suspendTarget.name}</h3>
              <button onClick={() => setSuspendTarget(null)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-zinc-400">The organisation's admins will be locked out and emailed. Add an optional reason to include in that email.</p>
              <textarea
                data-testid="sa-suspend-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Reason (optional) — e.g. suspected abuse, unpaid invoice…"
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
              />
              <div className="flex items-center justify-end gap-2">
                <button data-testid="sa-suspend-cancel" onClick={() => setSuspendTarget(null)} className="px-3.5 py-2 rounded-md text-sm font-medium text-zinc-400 hover:text-white transition-colors">Cancel</button>
                <button
                  data-testid="sa-suspend-confirm"
                  disabled={busy === suspendTarget.id}
                  onClick={async () => { const org = suspendTarget; setSuspendTarget(null); await doSuspend(org, reason); }}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                >
                  <Prohibit size={15} /> Suspend organisation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
